/**
 * Hook de Gerenciamento de Metadados
 * 
 * Gerencia metadados coletados do CorelDRAW:
 * - metadataSnapshot: Snapshot inicial de metadados
 * - lastSaveMeta: Último metadado salvo (com integridade)
 * - showMetadataModal: Flag do modal de metadados
 * 
 * Origem: linhas 62-87, 85-94, 370-451 do useSaveController.ts
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { storage } from '../../../lib/storage/storage';
import { Keys } from '../../../lib/storage/keys';
import { MetadataService } from '../../../lib/corel/actions/metadata';
import { logger } from '../../../lib/terminalLogger';

/**
 * Interface do último metadado salvo
 */
export interface LastSaveMeta {
  /**
   * Informações de integridade pós-salvamento
   */
  integridade?: {
    objetosVerificados?: number;
    metadadosOk?: number;
    metadadosPerdidos?: number;
    objetosProcessados?: number;
    guidsCriados?: number;
    guidsDuplicadosCorrigidos?: number;
    metadadosReparados?: number;
    faltantesAntes?: number;
    faltantesDepois?: number;
  };
  
  /**
   * Hash do arquivo salvo
   */
  hashAlgoritmo?: string;
  hashArquivo?: string;
  
  /**
   * Tamanho e timestamp
   */
  tamanhoBytes?: number;
  ultimaGravacaoUtc?: string;
  
  /**
   * Objetos salvos
   */
  objetos?: Array<{ 
    staticId?: string; 
    guid?: string;
    [key: string]: any;
  }>;
  
  /**
   * Snapshot do payload enviado
   */
  payload_snapshot?: any;
}

/**
 * Opções do hook useSaveMetadata
 */
export interface UseSaveMetadataOptions {
  /**
   * Snapshot inicial de metadados (vindo de props)
   */
  initialMetadataSnapshot?: any;
}

/**
 * Retorno do hook useSaveMetadata
 */
export interface UseSaveMetadataReturn {
  // Estados
  /**
   * Snapshot de metadados atual
   */
  metadataSnapshot: any;
  
  /**
   * Último metadado salvo (com integridade)
   */
  lastSaveMeta: LastSaveMeta | null;
  
  /**
   * Se o modal de metadados está aberto
   */
  showMetadataModal: boolean;
  
  // Refs (para acesso direto)
  /**
   * Ref do snapshot de metadados
   */
  metadataSnapshotRef: React.MutableRefObject<any>;
  
  /**
   * Ref do último metadado salvo
   */
  lastSaveMetaRef: React.MutableRefObject<LastSaveMeta | null>;
  
  // Actions
  /**
   * Define o snapshot de metadados
   */
  setMetadataSnapshot: (snapshot: any) => void;
  
  /**
   * Define o último metadado salvo
   */
  setLastSaveMeta: (meta: LastSaveMeta | null) => void;
  
  /**
   * Define se modal está aberto
   */
  setShowMetadataModal: (show: boolean) => void;
  
  /**
   * Coleta metadados do Corel com retry
   */
  collectMetadata: () => Promise<{ ok: boolean; payload?: any; error?: string }>;
  
  /**
   * Retorna hash do arquivo para idempotência
   */
  getFileHash: () => string | undefined;
}

/**
 * Hook de gerenciamento de metadados
 * 
 * Centraliza toda a lógica de metadados coletados do CorelDRAW.
 * 
 * Persistência:
 * - metadataSnapshot é persistido no session storage
 * - lastSaveMeta é mantido apenas em memória (ref)
 * 
 * @param options - Opções do hook
 * @returns Estados, refs e actions
 * 
 * @example
 * ```typescript
 * const metadata = useSaveMetadata({ 
 *   initialMetadataSnapshot: props.initialMetadataSnapshot 
 * });
 * 
 * // Coletar metadados
 * const result = await metadata.collectMetadata();
 * if (result.ok) {
 *   console.log('Metadados coletados:', result.payload);
 * }
 * 
 * // Obter hash para idempotência
 * const hash = metadata.getFileHash();
 * ```
 */
export function useSaveMetadata(options: UseSaveMetadataOptions = {}): UseSaveMetadataReturn {
  const { initialMetadataSnapshot } = options;

  // Inicializar com snapshot do storage ou inicial
  const initialSnapshot = initialMetadataSnapshot ?? 
    storage.get(Keys.export.initialMetadataSnapshot, { area: 'session' });

  // Estados
  const [metadataSnapshot, setMetadataSnapshotState] = useState<any>(initialSnapshot);
  const [lastSaveMeta, setLastSaveMetaState] = useState<LastSaveMeta | null>(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  // Refs para acesso direto (importante para callbacks)
  const metadataSnapshotRef = useRef<any>(initialSnapshot);
  const lastSaveMetaRef = useRef<LastSaveMeta | null>(null);

  /**
   * Define snapshot de metadados
   * 
   * Atualiza state, ref e persiste no storage
   */
  const setMetadataSnapshot = useCallback((snapshot: any) => {
    metadataSnapshotRef.current = snapshot;
    setMetadataSnapshotState(snapshot);

    // Persistir no session storage (TTL 10 minutos)
    try {
      storage.set(Keys.export.lastMetadataSnapshot, snapshot, {
        area: 'session',
        ttlMs: 10 * 60 * 1000
      });
    } catch (err) {
      // Erro ao persistir não deve quebrar o fluxo
      logger.warn('Falha ao persistir metadados no storage:', err);
    }
  }, []);

  /**
   * Define último metadado salvo
   * 
   * Atualiza state e ref
   */
  const setLastSaveMeta = useCallback((meta: LastSaveMeta | null) => {
    lastSaveMetaRef.current = meta;
    setLastSaveMetaState(meta);
  }, []);

  /**
   * Coleta metadados do Corel
   * 
   * Usa MetadataService.collectFullWithRetry do serviço central
   */
  const collectMetadata = useCallback(async (): Promise<{
    ok: boolean;
    payload?: any;
    error?: string;
  }> => {
    try {
      const result = await MetadataService.collectFullWithRetry(3, 500);

      // Se sucesso, atualizar snapshot
      if (result.ok && result.payload) {
        // Compatibilidade: envolver em estrutura esperada
        const wrappedPayload = { status: 'ok', result: result.payload };
        setMetadataSnapshot(wrappedPayload);
        
        // Também atualizar ref para compatibilidade
        if (metadataSnapshotRef) {
          if ('current' in metadataSnapshotRef) {
            metadataSnapshotRef.current = wrappedPayload;
          } else {
            Object.assign(metadataSnapshotRef, wrappedPayload);
          }
        }
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao coletar metadados';
      return {
        ok: false,
        error: errorMessage
      };
    }
  }, [setMetadataSnapshot, metadataSnapshotRef]);

  /**
   * Retorna hash do arquivo para idempotência
   * 
   * Extrai hash limpo do lastSaveMeta
   */
  const getFileHash = useCallback((): string | undefined => {
    const rawHash = (lastSaveMetaRef.current?.hashArquivo || '').toString();
    const cleaned = rawHash.replace(/[^a-fA-F0-9]/g, '');
    const isValidHash = cleaned.length === 32 || cleaned.length === 64;

    return isValidHash ? cleaned : undefined;
  }, []);

  // Sincronizar snapshot inicial quando mudar
  useEffect(() => {
    if (initialMetadataSnapshot) {
      setMetadataSnapshot(initialMetadataSnapshot);
    }
  }, [initialMetadataSnapshot, setMetadataSnapshot]);

  // Memoizar retorno para evitar recriação
  return useMemo(() => ({
    // Estados
    metadataSnapshot,
    lastSaveMeta,
    showMetadataModal,

    // Refs
    metadataSnapshotRef,
    lastSaveMetaRef,

    // Actions
    setMetadataSnapshot,
    setLastSaveMeta,
    setShowMetadataModal,
    collectMetadata,
    getFileHash,
  }), [
    metadataSnapshot,
    lastSaveMeta,
    showMetadataModal,
    metadataSnapshotRef,
    lastSaveMetaRef,
    setMetadataSnapshot,
    setLastSaveMeta,
    setShowMetadataModal,
    collectMetadata,
    getFileHash,
  ]);
}

